// src/storage/IndexerSettings.js

/**
 * User preferences for EventIndexer storage.
 * Persisted in localStorage.
 */
const STORAGE_KEY = 'mw3_indexer_settings';

const IndexerSettings = {
  /**
   * Get all settings.
   * @returns {Object}
   */
  get() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : this.getDefaults();
    } catch {
      return this.getDefaults();
    }
  },

  /**
   * Get default settings.
   * @returns {Object}
   */
  getDefaults() {
    return {
      storageEnabled: true,  // Allow IndexedDB storage
      maxStorageMB: 50,      // Max storage in MB (0 = unlimited)
    };
  },

  /**
   * Update settings.
   * @param {Object} updates - Partial settings to update
   */
  set(updates) {
    const current = this.get();
    const updated = { ...current, ...updates };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return updated;
  },

  /**
   * Check if IndexedDB storage is enabled.
   * @returns {boolean}
   */
  isStorageEnabled() {
    return this.get().storageEnabled;
  },

  /**
   * Clear all indexer data from IndexedDB.
   * @returns {Promise<void>}
   */
  async clearAllData() {
    // Find and delete all micro-web3 databases
    if (typeof indexedDB === 'undefined') return;

    const databases = await indexedDB.databases?.() || [];
    const mw3Databases = databases.filter(db =>
      db.name?.startsWith('micro-web3-events-') ||
      db.name?.includes('colasseum-')
    );

    for (const db of mw3Databases) {
      await new Promise((resolve, reject) => {
        const request = indexedDB.deleteDatabase(db.name);
        request.onsuccess = resolve;
        request.onerror = reject;
      });
    }
  },

  /**
   * Estimate current storage usage.
   * @returns {Promise<{used: number, quota: number}>} Bytes
   */
  async getStorageEstimate() {
    if (navigator.storage?.estimate) {
      return navigator.storage.estimate();
    }
    return { used: 0, quota: 0 };
  }
};

export default IndexerSettings;
